import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { tenantUserEditRules } from "../../forms/view-model-rules.js";

import { DataPending } from "./data-pending.js";
import { tenantUserUrls } from "./urls.js";

/** 編集画面の値（<c>GET /api/tenants/{契約}/users/edit</c>。サーバの TenantUserEditViewModel と同じ欄）。 */
export interface TenantUserEdit {
    readonly tenantId: number;
    readonly email: string;
    readonly userName: string;
    readonly admin: boolean;
    readonly roleLabel: string;
    /** 画面の題。一般の利用者が自分を直すときは「お名前の変更」、ほかは「利用者の編集」（サーバが決める）。 */
    readonly title: string;
    readonly canChangeAdmin: boolean;
    /** 管理者権限の欄の代わりに出す断り。対象が契約者のときだけ（サーバが決める）。ほかは null。 */
    readonly adminNote: string | null;
    readonly canDelete: boolean;
    /**
     * 「キャンセル」の行き先（サーバが決める）。契約者と管理者は利用者の一覧、
     * 一般の利用者は契約一覧（利用者の一覧を開けず、契約一覧の行から開いたため）。
     */
    readonly backUrl: string;
}

function EditForm({ user }: { readonly user: TenantUserEdit }): ReactElement {
    const form = useApiForm(tenantUserUrls.api.edit(user.tenantId, user.email), tenantUserEditRules);

    return (
        <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
            <FormSummary messages={form.summary} />

            <TextField
                label="お名前" name="UserName" required autoFocus defaultValue={user.userName}
                error={form.fieldError("UserName")}
            />

            {/* 変えられるかはサーバが決める（契約者の管理者権限は外せない） */}
            {user.canChangeAdmin
                ? (
                    <label className="mb-7 flex items-center gap-2 text-sm">
                        <input
                            className="form-check" id="Admin" name="Admin" type="checkbox" value="true"
                            defaultChecked={user.admin}
                        />
                        <input type="hidden" name="Admin" value="false" />
                        <span>管理者にする（利用者の招待・編集ができるようになります）</span>
                    </label>
                )
                : null}
            {/* 欄を出さない理由（対象が契約者のときだけ。一般の利用者が自分を直すときは出さない） */}
            {!user.canChangeAdmin && user.adminNote !== null
                ? <div className="alert-info mb-7">{user.adminNote}</div>
                : null}

            <div className="flex gap-3">
                <Link to={user.backUrl} className="btn-secondary">キャンセル</Link>
                <button type="submit" className="btn-primary grow" disabled={form.busy}>変更する</button>
            </div>
        </form>
    );
}

/** 利用者の編集。 */
export function UserEditPage({ tenantId, id }: {
    readonly tenantId: number;
    readonly id: string;
}): ReactElement {
    const data = useApiData<TenantUserEdit>(tenantUserUrls.api.edit(tenantId, id));
    // 題はサーバが決める（一般の利用者が自分を直すときは「お名前の変更」）
    usePageMeta(data.state === "ready" ? data.value.title : "利用者の編集");

    if (data.state !== "ready") {
        return <DataPending data={data} />;
    }

    const user = data.value;
    return (
        <div className="page max-w-lg">
            <div className="panel">
                <h1 className="page-title">{user.title}</h1>
                <p className="page-lead">{user.email}（{user.roleLabel}）</p>

                <EditForm user={user} />

                {user.canDelete
                    ? (
                        <div className="panel-footer flex justify-end">
                            {/* 解約と同じ形（赤）で右に置く。取り消せない操作を、ほかの操作と並べない */}
                            <Link to={tenantUserUrls.delete(user.tenantId, user.email)} className="btn-danger-quiet btn-sm">
                                この利用者を削除する
                            </Link>
                        </div>
                    )
                    : null}
            </div>
        </div>
    );
}
